import { produce } from 'immer'
import { signinRouteProps } from 'src/navigation/routeProps'
import {
  ABANDON_MFA_CHALLENGE,
  CONFIRM_COGNITO_SIGNIN_FAILURE,
  CONFIRM_COGNITO_SIGNIN_INIT,
  CONFIRM_COGNITO_SIGNIN_SUCCESS,
  ConfirmCognitoSigninActions,
  GET_COGNITO_SIGNIN_THEN_PUSH_FAILURE,
  GET_COGNITO_SIGNIN_THEN_PUSH_INIT,
  GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS,
  GetCognitoSigninThenPushActions,
  INIT_RESET_PASSWORD,
  POST_AMPLIFY_SIGNIN_FAILURE,
  POST_AMPLIFY_SIGNIN_INIT,
  POST_AMPLIFY_SIGNIN_SUCCESS,
  POST_AUTH_FLOW_TYPE_FAILURE,
  POST_COGNITO_LOOKUP_FAILURE,
  POST_LOOKUP_FAILURE,
  POST_LOOKUP_INIT,
  POST_LOOKUP_SUCCESS,
  POST_PHONE_VERIFICATION_FAILURE,
  POST_PHONE_VERIFICATION_INIT,
  POST_PHONE_VERIFICATION_SUCCESS,
  POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS,
  POST_SEND_PHONE_VERIFICATION_CODE_FAILURE,
  POST_SEND_PHONE_VERIFICATION_CODE_INIT,
  POST_SIGNIN_FAILURE,
  POST_SIGNIN_INIT,
  POST_SIGNIN_SUCCESS,
  POST_USER_RESEND_VERIFICATION_FAILURE,
  POST_USER_RESEND_VERIFICATION_INIT,
  POST_USER_RESEND_VERIFICATION_SUCCESS,
  POST_USER_VERIFICATION_FAILURE,
  POST_USER_VERIFICATION_INIT,
  POST_USER_VERIFICATION_SUCCESS,
  PostAmplifySigninActions,
  PostAuthFlowTypeActions,
  PostLookupActions,
  PostPhoneVerificationActions,
  PostSendPhoneVerificationCodeActions,
  PostSigninActions,
  PostUserResendVerificationActions,
  PostUserVerificationActions,
  RESET_MFA_CODE_STATE_ACTION,
  RESET_VERIFICATION_ERRORS,
  ResetVerificationErrorsAction,
  SET_USER_STATUS,
  SetUserStatusAction,
} from 'src/redux/actions/auth.actions'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface SigninState {
  isLookingUp: boolean
  isSigningIn: boolean
  isVerifyingUser: boolean
  lookupTopLevelError?: string
  lookupErrorDetailMap?: ErrorDetailMap<string>
  signinTopLevelError?: string
  signinErrorDetailMap?: ErrorDetailMap<string>
  verificationEmailResent: boolean
  resendVerificationTopLevelError?: string
  verifyEmailTopLevelError?: string
  verifyPhoneTopLevelError?: string
  verificationPhoneSent?: boolean
  verificationPhoneResent: boolean
  verifyPhoneVerificationLoading: boolean
  userEmailVerified: boolean
}

export const initialState: SigninState = {
  isLookingUp: false,
  isSigningIn: false,
  isVerifyingUser: false,
  verificationEmailResent: false,
  verificationPhoneSent: false,
  verificationPhoneResent: false,
  verifyPhoneVerificationLoading: false,
  userEmailVerified: false,
}

export type Actions =
  | PostLookupActions
  | PostSigninActions
  | PostAmplifySigninActions
  | PostUserVerificationActions
  | GetCognitoSigninThenPushActions
  | ConfirmCognitoSigninActions
  | PostUserResendVerificationActions
  | PostAuthFlowTypeActions
  | SetUserStatusAction
  | PostSendPhoneVerificationCodeActions
  | PostPhoneVerificationActions
  | ResetVerificationErrorsAction

export const CoreSigninReducer = (
  state = initialState,
  action: Actions,
): SigninState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case POST_LOOKUP_INIT: {
        draft.isLookingUp = true
        draft.lookupTopLevelError = undefined
        draft.lookupErrorDetailMap = undefined
        draft.verifyEmailTopLevelError = undefined
        draft.resendVerificationTopLevelError = undefined
        draft.signinTopLevelError = undefined
        draft.signinErrorDetailMap = undefined
        return draft
      }

      case POST_LOOKUP_SUCCESS: {
        draft.isLookingUp = false
        return draft
      }

      case POST_COGNITO_LOOKUP_FAILURE: {
        draft.isLookingUp = false
        draft.lookupTopLevelError = action.payload
        return draft
      }

      // error here
      case POST_LOOKUP_FAILURE: {
        draft.isLookingUp = false
        const errors = parseBackendErrors(action.payload)
        draft.lookupTopLevelError = errors.topLevelError
        draft.lookupErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case POST_AMPLIFY_SIGNIN_INIT:
      case GET_COGNITO_SIGNIN_THEN_PUSH_INIT:
      case POST_SIGNIN_INIT: {
        draft.isSigningIn = true
        draft.signinTopLevelError = undefined
        draft.signinErrorDetailMap = undefined
        return draft
      }

      case POST_AMPLIFY_SIGNIN_SUCCESS:
      case GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS:
      case POST_SIGNIN_SUCCESS: {
        draft.isSigningIn = false
        return draft
      }

      case GET_COGNITO_SIGNIN_THEN_PUSH_FAILURE:
      case POST_SIGNIN_FAILURE: {
        draft.isSigningIn = false
        draft.isLookingUp = false
        const errors = parseBackendErrors(action.payload)
        draft.signinTopLevelError = errors.topLevelError
        draft.signinErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case POST_AMPLIFY_SIGNIN_FAILURE: {
        draft.isSigningIn = false
        draft.isLookingUp = false
        draft.signinTopLevelError = action.payload
        return draft
      }

      case CONFIRM_COGNITO_SIGNIN_INIT:
      case POST_USER_VERIFICATION_INIT: {
        draft.isVerifyingUser = true
        draft.userEmailVerified = false
        draft.verifyEmailTopLevelError = undefined
        return draft
      }

      case CONFIRM_COGNITO_SIGNIN_SUCCESS:
      case POST_USER_VERIFICATION_SUCCESS: {
        draft.isVerifyingUser = false
        draft.userEmailVerified = true
        draft.signinTopLevelError = undefined
        return draft
      }

      case CONFIRM_COGNITO_SIGNIN_FAILURE: {
        draft.isVerifyingUser = false
        draft.verifyEmailTopLevelError = action.payload
        return draft
      }

      case POST_USER_VERIFICATION_FAILURE: {
        draft.isVerifyingUser = false
        draft.userEmailVerified = false
        const errors = parseBackendErrors(action.payload)
        draft.verifyEmailTopLevelError = errors.topLevelError
        return draft
      }

      case POST_USER_RESEND_VERIFICATION_INIT: {
        draft.verificationEmailResent = false
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_USER_RESEND_VERIFICATION_SUCCESS: {
        draft.verificationEmailResent = true
        return draft
      }

      case POST_USER_RESEND_VERIFICATION_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.resendVerificationTopLevelError = errors.topLevelError
        return draft
      }

      case POST_AUTH_FLOW_TYPE_FAILURE:
      case INIT_RESET_PASSWORD:
      case RESET_MFA_CODE_STATE_ACTION:
      case ABANDON_MFA_CHALLENGE: {
        draft.isSigningIn = false
        draft.isLookingUp = false
        return draft
      }

      case SET_USER_STATUS: {
        draft.signinTopLevelError = undefined
        return draft
      }

      // send phone verification code
      case POST_SEND_PHONE_VERIFICATION_CODE_INIT: {
        draft.verificationPhoneSent = true
        draft.verificationPhoneResent = false
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS: {
        draft.verificationPhoneResent = true
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_SEND_PHONE_VERIFICATION_CODE_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.resendVerificationTopLevelError = errors.topLevelError
        return draft
      }

      // verify phone verification code
      case POST_PHONE_VERIFICATION_INIT: {
        draft.verifyPhoneVerificationLoading = true
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }

      case POST_PHONE_VERIFICATION_SUCCESS: {
        draft.verifyPhoneVerificationLoading = false
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }

      case POST_PHONE_VERIFICATION_FAILURE: {
        draft.verifyPhoneVerificationLoading = false
        const errors = parseBackendErrors(action.payload)
        draft.verifyPhoneTopLevelError = errors.topLevelError
        return draft
      }

      case RESET_VERIFICATION_ERRORS: {
        draft.verifyEmailTopLevelError = undefined
        draft.resendVerificationTopLevelError = undefined
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }
    }
  })
}

const SigninReducer = createBackgroundReducer(
  signinRouteProps,
  initialState,
  CoreSigninReducer,
)

export default SigninReducer
